import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { NumberInputField } from './atoms/NumberInputField'
import { SliderField } from './atoms/SliderField'
import { DynamicRowList } from './molecules/DynamicRowList'
import { ChainIndicator } from './molecules/ChainIndicator'
import { calcularCPAMaximo } from '../lib/cpaCalculations'
import { calcularPlanejamento } from '../lib/planejamentoCalculations'
import { calcularMetricasComerciais } from '../lib/metricasComerciaisCalculations'
import { useRaioXState } from '../hooks/useRaioXState'
import { cpaMaximoDefaultValues, planejamentoDefaultValues, metricasComerciaisDefaultValues } from '../constants'
import { formatCurrency, formatPercent } from '../lib/formatters'
import type { VariableCost, CalculatorResult } from '../types'

export function PlanejamentoPage() {
  // Section A: CPA Máximo
  const cpaState = useRaioXState(cpaMaximoDefaultValues)
  const [custosVariaveis, setCustosVariaveis] = useState<VariableCost[]>([])

  // Section B: Planejamento
  const planState = useRaioXState(planejamentoDefaultValues)

  // Section C: Simulador (metricas comerciais)
  const simState = useRaioXState(metricasComerciaisDefaultValues)

  const cpaResults = useMemo(() => {
    return calcularCPAMaximo({ ...cpaState.values, custosVariaveis })
  }, [cpaState.values, custosVariaveis])

  const planInputs = useMemo(() => ({
    ...planState.values,
    precoProduto: cpaState.values.precoProduto,
  }), [cpaState.values.precoProduto, planState.values])

  const planResults = useMemo(() => {
    return calcularPlanejamento(planInputs)
  }, [planInputs])

  const investimentoCalculado = planResults.find((r) => r.label === 'Investimento Necessário')

  const simInputs = useMemo(() => ({
    ...simState.values,
    investimento: investimentoCalculado?.value ?? simState.values.investimento,
    taxaLeadAgendamento: planInputs.taxaLeadAgendamento,
    taxaAgendadaRealizada: planInputs.taxaAgendadaRealizada,
    taxaConsultaVenda: planInputs.taxaConsultaVenda,
    ticketMedio: planInputs.precoProduto,
  }), [investimentoCalculado?.value, planInputs, simState.values])

  const simResults = useMemo(() => {
    return calcularMetricasComerciais(simInputs)
  }, [simInputs])

  const cpaBreakeven = cpaResults.find((r) => r.label === 'CPA Break-even (1x)')
  const lucroEstimado = planResults.find((r) => r.label === 'Lucro Estimado')

  return (
    <div className="flex flex-col gap-8 max-w-[900px] mx-auto">
      <div>
        <h2 className="text-xl font-semibold tracking-[-0.022em] text-foreground">
          Planejamento Estratégico
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Calculadoras encadeadas: CPA → Metas → Simulação
        </p>
      </div>

      {/* Section A: CPA Máximo */}
      <section>
        <SectionHeader letter="A" title="CPA Máximo" subtitle="Calcule o teto de custo por aquisição" outputValue={cpaBreakeven?.formattedValue} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card border border-border rounded-2xl">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.06em]">
                Parâmetros
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <NumberInputField
                id="cpa-preco"
                label="Preço do Produto"
                prefix="R$"
                value={cpaState.values.precoProduto}
                onChange={(v) => cpaState.updateValue('precoProduto', v)}
              />
              <NumberInputField
                id="cpa-meta"
                label="Meta de Vendas"
                value={cpaState.values.metaVendas}
                onChange={(v) => cpaState.updateValue('metaVendas', v)}
              />
              <NumberInputField
                id="cpa-taxa-pct"
                label="Taxa da Plataforma (%)"
                suffix="%"
                value={cpaState.values.taxaPlataformaPercent}
                onChange={(v) => cpaState.updateValue('taxaPlataformaPercent', v)}
              />
              <NumberInputField
                id="cpa-taxa-fixo"
                label="Taxa Fixa da Plataforma"
                prefix="R$"
                value={cpaState.values.taxaPlataformaFixo}
                onChange={(v) => cpaState.updateValue('taxaPlataformaFixo', v)}
              />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Custos Variáveis</p>
                <DynamicRowList rows={custosVariaveis} onChange={setCustosVariaveis} />
              </div>
            </CardContent>
          </Card>
          <InlineResults results={cpaResults} />
        </div>
      </section>

      <ChainIndicator label="Preço do produto alimenta o planejamento" />

      {/* Section B: Planejamento */}
      <section>
        <SectionHeader
          letter="B"
          title="Planejamento Estratégico"
          subtitle="Quantos leads e quanto investir para atingir a meta"
          outputValue={investimentoCalculado?.formattedValue}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card border border-border rounded-2xl">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.06em]">
                Parâmetros
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <NumberInputField
                id="plan-faturamento"
                label="Faturamento Desejado"
                prefix="R$"
                value={planState.values.faturamento}
                onChange={(v) => planState.updateValue('faturamento', v)}
              />
              <DerivedParameter label="Preço do Produto" value={formatCurrency(planInputs.precoProduto)} />
              <SliderField
                label="Lead → Agendamento"
                value={planState.values.taxaLeadAgendamento}
                onChange={(v) => planState.updateValue('taxaLeadAgendamento', v)}
                min={1} max={100} step={1}
                formatValue={(v) => formatPercent(v, 0)}
              />
              <SliderField
                label="Agendada → Realizada"
                value={planState.values.taxaAgendadaRealizada}
                onChange={(v) => planState.updateValue('taxaAgendadaRealizada', v)}
                min={1} max={100} step={1}
                formatValue={(v) => formatPercent(v, 0)}
              />
              <SliderField
                label="Consulta → Venda"
                value={planState.values.taxaConsultaVenda}
                onChange={(v) => planState.updateValue('taxaConsultaVenda', v)}
                min={1} max={100} step={1}
                formatValue={(v) => formatPercent(v, 0)}
              />
              <NumberInputField
                id="plan-preco-lead"
                label="Preço Médio do Lead"
                prefix="R$"
                value={planState.values.precoLead}
                onChange={(v) => planState.updateValue('precoLead', v)}
              />
            </CardContent>
          </Card>
          <InlineResults results={planResults} highlight={lucroEstimado?.label} />
        </div>
      </section>

      <ChainIndicator label="Investimento calculado alimenta a simulação" />

      {/* Section C: Simulador */}
      <section>
        <SectionHeader
          letter="C"
          title="Simulador do Funil"
          subtitle="Projeção completa do funil com os parâmetros calculados"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card border border-border rounded-2xl">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.06em]">
                Parâmetros
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-4">
              <DerivedParameter label="Investimento" value={formatCurrency(simInputs.investimento)} />
              <SliderField
                label="CPM"
                value={simState.values.cpm}
                onChange={(v) => simState.updateValue('cpm', v)}
                min={1} max={200} step={1}
                formatValue={formatCurrency}
              />
              <SliderField
                label="CTR"
                value={simState.values.ctr}
                onChange={(v) => simState.updateValue('ctr', v)}
                min={0.1} max={10} step={0.1}
                formatValue={(v) => formatPercent(v, 1)}
              />
              <SliderField
                label="Clique → Lead"
                value={simState.values.taxaCliqueLead}
                onChange={(v) => simState.updateValue('taxaCliqueLead', v)}
                min={1} max={100} step={1}
                unit="%"
              />
              <DerivedParameter label="Lead → Agendamento" value={formatPercent(simInputs.taxaLeadAgendamento, 0)} />
              <DerivedParameter label="Agendada → Realizada" value={formatPercent(simInputs.taxaAgendadaRealizada, 0)} />
              <DerivedParameter label="Consulta → Venda" value={formatPercent(simInputs.taxaConsultaVenda, 0)} />
              <DerivedParameter label="Ticket Médio" value={formatCurrency(simInputs.ticketMedio)} />
            </CardContent>
          </Card>
          <InlineResults results={simResults} />
        </div>
      </section>
    </div>
  )
}

function DerivedParameter({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-xs font-semibold tabular-nums font-mono text-foreground">
        {value}
      </span>
    </div>
  )
}

function SectionHeader({
  letter,
  title,
  subtitle,
  outputValue,
}: {
  letter: string
  title: string
  subtitle?: string
  outputValue?: string
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
        <span className="text-xs font-bold text-primary">{letter}</span>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-foreground tracking-[-0.022em]">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {outputValue && (
        <Badge className="bg-primary/15 text-primary border-0 tabular-nums font-mono text-xs">
          {outputValue}
        </Badge>
      )}
    </div>
  )
}

function InlineResults({ results, highlight }: { results: CalculatorResult[]; highlight?: string }) {
  return (
    <Card className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border/60">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Resultados
        </span>
      </div>
      <div className="px-4 py-1">
        {results.map((r, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center justify-between py-2.5 border-b border-border/50 last:border-0',
              r.label === highlight && 'bg-primary/[0.04] -mx-4 px-4 rounded',
            )}
          >
            <span className="text-xs text-muted-foreground">{r.label}</span>
            <span
              className={cn(
                'text-sm font-semibold tabular-nums font-mono',
                r.status === 'success' && 'text-success',
                r.status === 'warning' && 'text-warning',
                r.status === 'danger' && 'text-destructive',
                (!r.status || r.status === 'accent') && 'text-foreground',
              )}
            >
              {r.formattedValue}
            </span>
          </div>
        ))}
      </div>
    </Card>
  )
}
